import ErrorHandler from 'store/utils/ErrorHandler';
import Notify from 'store/utils/Notify';
import { getIntent } from 'store/services/intents/selectors';
import { getAddedUtterances, getDeletedUtterances } from './selectors';
import * as types from './actionTypes';
import * as api from './api';

/**
 * Get utterances
 */
export const getUtterancesList = () => async (dispatch, getState) => {
  try {
    await dispatch({
      type: types.GET_INTENT_UTTERANCE_LIST_REQUEST,
    });

    const intentId = getIntent(getState()).id;

    const { data } = await api.getUtterancesRequest(intentId);

    await dispatch({
      type: types.GET_INTENT_UTTERANCE_LIST_SUCCESS,
      payload: {
        utterances: data.results,
      },
    });
  } catch (error) {
    await dispatch({
      type: types.GET_INTENT_UTTERANCE_LIST_ERROR,
      payload: {
        error: error.response.data,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Add utterance
 *
 * @param data
 * @returns {Function}
 */
export const addUtterance = data => async (dispatch, getState) => {
  try {
    await dispatch({
      type: types.ADD_INTENT_UTTERANCE_REQUEST,
    });

    const intentId = getIntent(getState()).id;

    await api.addUtteranceRequest(intentId, data);

    await dispatch({
      type: types.ADD_INTENT_UTTERANCE_SUCCESS,
    });

    await dispatch(getUtterancesList());

    Notify.success({
      title: 'Utterance was created successfully',
    });
  } catch (error) {
    await dispatch({
      type: types.ADD_INTENT_UTTERANCE_ERROR,
      payload: {
        error: error.response.data,
      },
    });

    Notify.error({
      title: 'Fail to add Utterance',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Update utterances
 *
 * @param id
 * @param data
 * @returns {Function}
 */
export const updateUtterance = (id, data) => async (dispatch, getState) => {
  try {
    await dispatch({
      type: types.UPDATE_INTENT_UTTERANCE_REQUEST,
    });

    const intentId = getIntent(getState()).id;

    await api.updateUtteranceRequest(intentId, id, data);

    await dispatch({
      type: types.UPDATE_INTENT_UTTERANCE_SUCCESS,
    });

    await dispatch(getUtterancesList());

    Notify.success({
      title: 'Utterance was updated successfully',
    });
  } catch (error) {
    await dispatch({
      type: types.UPDATE_INTENT_UTTERANCE_ERROR,
      payload: {
        error: error.response.data,
      },
    });

    Notify.error({
      title: 'Fail to update Utterance',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Delete utterances
 *
 * @param id
 * @returns {Function}
 */
export const deleteUtterance = id => async (dispatch, getState) => {
  try {
    await dispatch({
      type: types.DELETE_INTENT_UTTERANCE_REQUEST,
    });

    const intentId = getIntent(getState()).id;

    await api.deleteUtteranceRequest(intentId, id);

    await dispatch({
      type: types.DELETE_INTENT_UTTERANCE_SUCCESS,
    });

    await dispatch(getUtterancesList());

    Notify.success({
      title: 'Utterance was deleted successfully',
    });
  } catch (error) {
    await dispatch({
      type: types.DELETE_INTENT_UTTERANCE_ERROR,
      payload: {
        error: error.response.data,
      },
    });

    Notify.error({
      title: 'Fail to delete Utterance',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Update utterances
 *
 * @param utterances
 * @returns {Function}
 */
export const updateUtterances = utterances => async (dispatch, getState) => {
  try {
    await dispatch({
      type: types.UPDATE_INTENT_UTTERANCES_REQUEST,
    });

    const state = getState();
    const addedUtterances = getAddedUtterances(utterances)(state);
    const deletedUtterances = getDeletedUtterances(utterances)(state);

    const intentId = getIntent(state).id;

    /* eslint-disable no-await-in-loop */
    for (const utterance of addedUtterances) {
      // eslint-disable-next-line no-await-in-loop
      await api.addUtteranceRequest(intentId, {
        utterance: utterance.utterance,
      });
    }

    for (const utterance of deletedUtterances) {
      // eslint-disable-next-line no-await-in-loop
      await api.deleteUtteranceRequest(intentId, utterance.id);
    }

    await dispatch({
      type: types.UPDATE_INTENT_UTTERANCES_SUCCESS,
    });

    await dispatch(getUtterancesList());
    Notify.success({
      title: 'Utterances were updated successfully',
    });
  } catch (error) {
    await dispatch({
      type: types.UPDATE_INTENT_UTTERANCES_ERROR,
      payload: {
        error: error.response.data,
      },
    });

    Notify.error({
      title: 'Fail to update Utterances',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};
